/* Dateien teilen: Zustand und Ablauf für die Auswahlleiste (Singleton, nur im Speicher).
 *
 * Der Ablauf selbst (Laden mit Zeitgrenze, Teilen-Menü oder Download) steht in domain/shareFlow.js; hier hängen
 * nur Zustand (Fortschritt, Abbruch, "Notknopf") und der Zugriff auf den Speicher daran.
 * Die Player Card wird erzeugt (useProfilePdf.js) und über runWith() geteilt; run() lädt beliebige Dateien.
 */

import { ref } from 'vue';
import { downloadFile } from '../export/download.js';
import { createCancel, loadFiles, deliverFiles, shareAgain } from '../domain/shareFlow.js';

const busy = ref(false);
/** { done, total } während des Ladens, sonst null */
const progress = ref(null);
/** Dateien, die nach abgelaufener Berührung auf einen frischen Tipp warten (Notknopf "Share {n} files") */
const pending = ref(null);

let current = null;

export function useShareFiles() {
  /**
   * @param {Array<{ name: string, filename: string, load: () => Promise<Blob> }>} items
   * @returns {Promise<{ status: 'shared'|'dismissed'|'needsTap'|'downloaded'|'cancelled'|'none'|'busy',
   *                     count: number, failed: string[] }>}
   */
  async function run(items) {
    return runWith((ctx) => loadFiles(items, ctx));
  }

  /**
   * Wie run(), aber die Dateien entstehen durch `producer` (Stufe 4: Profil-PDF erzeugen). Der Erzeuger
   * bekommt { cancel, onProgress } und liefert { files: [{ filename, blob }], failed: string[], cancelled }.
   */
  async function runWith(producer) {
    if (busy.value) return { status: 'busy', count: 0, failed: [] };
    busy.value = true;
    pending.value = null;
    current = createCancel();
    try {
      const loaded = await producer({
        cancel: current,
        onProgress: (done, total) => { progress.value = { done, total }; },
      });
      if (loaded.cancelled) return { status: 'cancelled', count: 0, failed: loaded.failed };
      if (!loaded.files.length) return { status: 'none', count: 0, failed: loaded.failed };
      progress.value = null;
      const out = await deliverFiles(loaded.files, { download: downloadFile });
      if (out.status === 'needsTap') pending.value = out.files;
      return { status: out.status, count: out.count, failed: loaded.failed };
    } finally {
      busy.value = false;
      progress.value = null;
      current = null;
    }
  }

  /** Laden abbrechen (jederzeit); die Fortschrittsanzeige verschwindet sofort. */
  function cancel() {
    if (current) current.cancel();
    progress.value = null;
  }

  /** Notknopf: die geladenen Dateien mit einem frischen Tipp noch einmal teilen. */
  async function shareNow() {
    const files = pending.value;
    if (!files || !files.length) return { status: 'none', count: 0 };
    pending.value = null;
    return shareAgain(files, { nav: globalThis.navigator, download: downloadFile });
  }

  function dismissPending() {
    pending.value = null;
  }

  return { busy, progress, pending, run, runWith, cancel, shareNow, dismissPending };
}
